/* eslint-disable max-lines */
import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getByID, subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import {
  BuchungsheldLitElement,
  type BuchungsheldLitElementHost,
} from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { book } from '@adornis/buchungsheld-db/api/booking.js';
import { subscribeParticipationsActiveByUser } from '@adornis/buchungsheld-db/api/participation.js';
import { areTimeSlotOverlapping } from '@adornis/buchungsheld-db/datetime/generate-start-times-for-multiple-overlapping-timeslots.js';
import type { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Bundle } from '@adornis/buchungsheld-db/entities/Bundle.js';
import { RegistrationColorDefinitions } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import {
  Einzelberatung,
  EinzelberatungSlot,
  EinzelberatungSlotConfiguration,
} from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Product, ProductFilter } from '@adornis/buchungsheld-db/entities/products.js';
import {
  BookingResult,
  RegistrationData,
  RegistrationEinzelberatungData,
  RegistrationWorkshopData,
  type RegistrationProductData,
} from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { getBillData } from '@adornis/buchungsheld-db/global-helpers.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import type { ChemistryController } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { Price } from '@adornis/payments/db/prices.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { _subscribeCurrentUser, currentUser, updateCurrentUser } from '@adornis/users/client/currentUser.js';
import { createContext } from '@lit/context';
import { type ReactiveElement } from 'lit';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
  type Observable,
} from 'rxjs';
import type { InstitutionOffer, ProductFilterOptions } from '../api/institution-offer.js';

function setSearchParam(key: string, value: string) {
  // console.trace('set search param', key, value);
  routingState.searchParams.set(key, value);
}

export const activeProductsSelectionSet = {
  _id: 1,
  categoryID: 1,
  category: {
    _id: 1,
    name: 1,
    timeframe: Timeframe.allFields,
    sortingWeight: 1,
  },
  description: 1,
  descriptionHTML: 1,
  isLDAPOnly: 1,
  hasPrice: 1,
  institutionID: 1,
  isPaymentNeeded: 1,
  name: 1,
  prices: Price.allFields,
  sortingWeight: 1,
  timeframeID: 1,
  timeframe: Timeframe.allFields,
  slotConfig: EinzelberatungSlotConfiguration.allFields,
  slots: EinzelberatungSlot.allFields,
  capacity: 1,
  leftCapacity: 1,
  hideCapacity: 1,
  unitAmount: 1,
  appointments: Appointment.allFields,
  language: 1,
  presenceType: 1,
  topic: 1,
  startDate: 1,
  translatedDescriptionHTML: selectionSet(() => TextMultipleLangItem, 1),
  productEntryRequirementLabels: 1,
  settings: {
    paymentMethods: 1,
    inheritedPaymentMethods: 1,
    courseLocation: 1,
    inheritedCourseLocation: 1,
  },
};

export const activeWorkshopSetsSelectionSet = {
  id: 1,
  name: 1,
  categoryID: 1,
  timeframeID: 1,
  translatedDescriptionHTML: TextMultipleLangItem.allFields,
  workshops: {
    _id: 1,
    name: 1,
    timeframe: Timeframe.allFields,
    start: 1,
    end: 1,
    appointments: Appointment.allFields,
    translatedDescriptionHTML: TextMultipleLangItem.allFields,
    hasPrice: 1,
    price: Price.allFields,
    prices: Price.allFields,
    capacity: 1,
    leftCapacity: 1,
    settings: { courseLocation: 1, inheritedCourseLocation: 1 },
    language: 1,
    presenceType: 1,
    topic: 1,
    hideCapacity: 1,
    category: { _id: 1, name: 1, timeframe: Timeframe.allFields },
    productEntryRequirementLabels: 1,
    isLDAPOnly: 1,
  },
};

export const registrationBundleSelectionSet = {
  _id: 1,
  name: 1,
  hasPrice: 1,
  productIDs: 1,
  price: { unitAmount: 1 },
  products: { _id: 1, name: 1, unitAmount: 1, appointments: { isNonBlocking: 1, start: 1, end: 1 } },
  isActive: 1,
} as const;

export const registrationInstitutionSelectionSet = {
  _id: 1,
  isLDAPOnly: 1,
  maintenanceMode: 1,
  maximumParallelSignups: 1,
  maintenanceModeRender: 1,
  sortProductsByTime: 1,
  termsOfUse: 1,
  productPage: 1,
  productPageWorkshopCard: 1,
  productPageCounselingCard: 1,
  productPageWorkshopSetCard: 1,
  globalSettings: { termsOfUse: 1 },
  whatsappEnabled: 1,
  settings: {
    colorDefinitions: {
      registrationColorDefinitions: RegistrationColorDefinitions.allFields,
    },
    inheritedColorDefinitions: {
      value: {
        registrationColorDefinitions: RegistrationColorDefinitions.allFields,
      },
    },
  },
} as const;

type GetObservableType<TObservable extends Observable<any>> = TObservable extends Observable<infer Tinner>
  ? Tinner
  : never;
const steps = [
  'workshop', // choose procts
  'auth', // only for login; gets skipped first and only gets used when user request to login
  'personal', // register / enter stammdaten
  'loginInNewTab', // login in new tab
  'verifyEmail', // verify e-mail if not already verified
  'terms', // accept terms
  'payment', // pay (if necessary)
  'booked', // this state is entered after stripe payment and immediatly routes to summery screen
  'paid', // TODO: figure out what this stae is used for; can it be removed?
  'maintainanceBlock', // maybe block whole prcess when institution-maintainance-mode is activated
] as const;

type Tstep = (typeof steps)[number];

export const registrationControllerContext = createContext<RegistrationController>('registration-controller');
export const institutionOfferContext = createContext<InstitutionOffer>('institution-offer');
export const productFilterContext = createContext<ProductFilter>('product-filter');
export const productFilterOptionsContext = createContext<ProductFilterOptions>('product-filter-options');

export class RegistrationController implements ChemistryController {
  host: BuchungsheldLitElementHost;
  subject: Subject<boolean>;

  // loading
  _institutionLoading = true;
  set institutionLoading(newValue: boolean) {
    this._institutionLoading = newValue;
    this._emitUpdate();
  }
  get institutionLoading() {
    return this._institutionLoading;
  }

  _userLoading = true;
  set userLoading(newValue: boolean) {
    this._userLoading = newValue;
    this._emitUpdate();
  }
  get userLoading() {
    return this._userLoading;
  }

  _paymentLoading = false;
  set paymentLoading(newValue: boolean) {
    this._paymentLoading = newValue;
    this._emitUpdate();
  }
  get paymentLoading() {
    return this._paymentLoading;
  }

  _nextStepProcessing = false;
  set nextStepProcessing(newValue: boolean) {
    this._nextStepProcessing = newValue;
    this._emitUpdate();
  }
  get nextStepProcessing() {
    return this._nextStepProcessing;
  }

  _bookingProcessing = false;
  set bookingProcessing(newValue: boolean) {
    this._bookingProcessing = newValue;
    this._emitUpdate();
  }
  get bookingProcessing() {
    return this._bookingProcessing;
  }

  get isLoading() {
    return (
      this.institutionLoading ||
      this.userLoading ||
      this.paymentLoading ||
      this.nextStepProcessing ||
      this.bookingProcessing
    );
  }

  // institution
  institutionID$ = routingState.searchParams.get('institution').pipe(
    map(it => it as Maybe<string>),
    distinctUntilChanged(),
    shareReplay(1),
  );

  institution$ = this.institutionID$.pipe(
    distinctUntilChanged(),
    tap(() => (this.institutionLoading = true)),
    switchMap(id => (id ? subscribeByID(Institution, id)(registrationInstitutionSelectionSet) : of(null))),
    tap(() => (this.institutionLoading = false)),
    shareReplay(1),
  );
  institutionID!: string;
  institution!: NarrowedEntity<Institution, typeof registrationInstitutionSelectionSet>;

  // used by embed to only show workshops from one category
  categorieConstraints$ = new BehaviorSubject([] as string[]);

  selectedBundles: Maybe<NarrowedEntity<Bundle, typeof registrationBundleSelectionSet>[]>;

  bundleMappedByProductID: Maybe<{ [k in string]: Bundle }>;

  // registrationData
  registrationData$ = routingState.searchParams.get('data').pipe(
    // filter(Boolean),
    map(dataString => (dataString ? this._parseDataQueryParamRegistrationData(dataString) : null)),
    shareReplay(1),
  );
  registrationData: Maybe<RegistrationData>;

  // currentUser
  static currentUser$ = currentUser<User>().pipe(shareReplay(1));
  currentUser: Maybe<User>;

  // participation
  participationIDs$ = routingState.searchParams.get('participationIDs').pipe(
    map(it => it?.split(',') ?? []),
    shareReplay(1),
  );
  participationIDs: string[] = [];

  bookedAndBlockingTimeslots$ = merge(
    of([]),
    RegistrationController.currentUser$.pipe(
      filter(Boolean),
      switchMap(user =>
        Participation.getUserTimeSlotsOfActiveParticipations(user._id)({ start: 1, end: 1, isNonBlocking: 1 }),
      ),
      map(slots => slots.filter(slot => !slot.isNonBlocking)),
    ),
  );
  bookedAndBlockingTimeslots: TimeSlot[] = [];

  // current registration step
  step$ = routingState.searchParams.get('step').pipe(
    filter(Boolean),
    map(step => {
      if (this.error instanceof InvalidStepError) this.error = null;
      if (steps.includes(step as Tstep)) return step as Tstep;

      this.error = new InvalidStepError();
      return null;
    }),
    filter(Boolean),
    shareReplay(1),
  );
  step: Tstep = 'workshop';

  // current registration step
  stepInfo$ = combineLatest([
    this.step$.pipe(filter(Boolean)),
    this.institution$.pipe(filter(Boolean)),
    currentUser<User>(),
  ]).pipe(
    map(([step, institution, user]) => {
      const isLDAPOnly = institution.isLDAPOnly;
      const hasLdapOnlyWorkshop = this.selectedProducts.filter(p => p.isLDAPOnly).length;
      const requiresLDAP = isLDAPOnly || hasLdapOnlyWorkshop;
      const userIsLoggedIn = !!user;
      const accountIsLDAP = !!user?.isLdapAccount;
      const ldapBlocked = userIsLoggedIn && requiresLDAP && !accountIsLDAP;
      const needsPayment =
        this.selectedProducts.some(product => product.hasPrice) ||
        this.selectedBundles?.some(bundle => bundle.hasPrice);
      const userMailIsConfirmed = user?.emailConfirmed ?? false;

      const activeSteps = ((): Tstep[] => {
        if (institution.maintenanceMode) return ['maintainanceBlock'];

        return steps.filter(_step => {
          if (_step === 'auth') return step === 'auth'; // only show when on it
          if (_step === 'maintainanceBlock') return false;
          if (_step === 'payment') return needsPayment;
          if (_step === 'verifyEmail') return !userMailIsConfirmed;
          if (_step === 'loginInNewTab') return step === 'loginInNewTab';
          return true;
        });
      })();

      const stepIndex = activeSteps.indexOf(step);
      return {
        activeSteps,
        step,
        stepIndex,
        requiresLDAP,
        ldapBlocked,
        userIsLoggedIn,
      };
    }),
    shareReplay(1),
  );

  stepInfo!: GetObservableType<typeof this.stepInfo$>;

  // selectedProducts
  selectedProducts$ = this.registrationData$.pipe(
    filter(isDefined),
    map(registrationData => registrationData.productData.map(productData => productData.productID)),
    switchMap(ids => getByID(Product, ids)(activeProductsSelectionSet)),
  );

  selectedProducts: Product[] = [];

  // selected Appointments/Timeslots
  selectedAppointments$ = this.registrationData$.pipe(
    map(registrationData => {
      return registrationData?.productData ?? [];
    }),
  );
  selectedAppointments: RegistrationProductData[] = [];

  // all blocked Timeslots
  selectedBlockingTimeslots$ = this.selectedAppointments$.pipe(
    switchMap(async selectedAppointments => {
      const products = await getByID(
        Product,
        selectedAppointments.map(ap => ap.productID).filter(isDefined) as string[],
      )({ appointments: { start: 1, end: 1, isNonBlocking: 1 }, slots: { start: 1, end: 1, isNonBlocking: 1 } });

      const selectedBlockingTimeslots = selectedAppointments
        .map(ap => {
          const product = products.find(product => product._id === ap.productID);
          if (!product) throw new Error('product not found ' + ap.productID);
          if (product instanceof Workshop) return product.appointments;
          if (product instanceof Einzelberatung) {
            const slotID = (ap as RegistrationEinzelberatungData).timeslotID;
            if (!slotID) throw new Error('slotID not found');
            return [product.slots.find(sl => sl._id === slotID)!];
          }

          throw new Error('unreachable');
        })
        .flat();

      // filter out duplicates
      return selectedBlockingTimeslots.reduce((acc, timeslot) => {
        if (!!acc.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
        return [...acc, timeslot];
      }, [] as TimeSlot[]);
    }),
  );
  selectedBlockingTimeslots: TimeSlot[] = [];

  allBlockingTimeslots$ = combineLatest([this.selectedBlockingTimeslots$, this.bookedAndBlockingTimeslots$]).pipe(
    map(([selectedBlockingTimeslots, bookedAndBlockingTimeslots]) => {
      return [...selectedBlockingTimeslots, ...bookedAndBlockingTimeslots];
    }),
    map(timeslots => {
      // filter out duplicates
      return timeslots.reduce((acc, timeslot) => {
        if (!!acc.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
        return [...acc, timeslot];
      }, [] as TimeSlot[]);
    }),
  );
  allBlockingTimeslots: TimeSlot[] = [];

  activeParticipations$ = combineLatest([currentUser(), this.institutionID$]).pipe(
    switchMap(([user, institutionID]) => {
      if (!user) return [];
      return subscribeParticipationsActiveByUser(user._id)({
        userID: 1,
        productID: 1,
        product: { institutionID: 1 },
        state: 1,
      }).pipe(map(participations => participations.filter(p => p.product?.institutionID === institutionID)));
    }),
    map(participations =>
      participations.filter(p => ![ParticipationState.LEFT, ParticipationState.SUSPENDED].includes(p.state)),
    ),
    shareReplay(1),
  );
  activeParticipations: Participation[] = [];

  // available payment Methods
  // TODO: move that to payment page
  availablePaymentMethods$ = this.selectedProducts$.pipe(
    map(products => {
      const p1 = products[0];
      if (!p1) return [];

      const paymentPlugin = getPluginForScope('payment', p1.institutionID);
      const paymentMethods = products.reduce<string[]>((acc, p) => {
        // make sure to only include payment-methods that are supported by every product
        const paymentMethods = p.settings.paymentMethods ?? p.settings.inheritedPaymentMethods?.[1];
        if (!paymentMethods) return acc; // if there are no constraints => allow all
        return acc.filter(pm => paymentMethods.includes(pm));
      }, paymentPlugin.paymentMethods);

      return paymentMethods;
    }),
  );

  private _error: Maybe<Error>;
  set error(newValue: Maybe<Error>) {
    this._error = newValue;
    this._emitUpdate();
  }
  get error() {
    return this._error;
  }
  setPaymentLinkError() {
    void XSnackbar.show(this.host._translation.translate('err', 'something_went_wrong'));
    this._bookingProcessing = false;
    this._emitUpdate();
  }

  isTimeSlotoverlappingWithOtherTimeslots(timeslot: TimeSlot) {
    // compare timeslots individually to prevent error when there are overlapping timeslots in already booked appointments
    for (const bookedTimeslot of this.allBlockingTimeslots) {
      if (timeslot._id === bookedTimeslot._id) continue; // do not compare timeslot with itself
      if (areTimeSlotOverlapping([timeslot, bookedTimeslot])) return true;
    }
    return false;
  }

  get hasOverlappingError() {
    if (areTimeSlotOverlapping(this.selectedBlockingTimeslots)) return true;

    // compare timeslots individually to prevent error when there are overlapping timeslots in already booked appointments
    for (const bookedTimeslot of this.bookedAndBlockingTimeslots) {
      const timeslots = [...this.selectedBlockingTimeslots, bookedTimeslot].reduce((acc, timeslot) => {
        // filter out duplicates
        if (!!acc.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
        return [...acc, timeslot];
      }, []);
      if (areTimeSlotOverlapping(timeslots)) return true;
    }

    return false;
  }

  get hasOrderingAlreadyBookedProductError() {
    // in other steps this may be fine. For example in booked-overview
    if (this.stepInfo.step === 'personal') {
      const alreadybookedproducts = this.activeParticipations.map(participation => participation.productID);
      return !!this.selectedProducts.find(product => alreadybookedproducts.includes(product._id));
    }

    return false;
  }

  private _propertyName?: string;
  get propertyName() {
    if (!this._propertyName) {
      let propertyName: string | undefined;
      for (const propName in this.host) {
        if (this.host[propName] === this) {
          propertyName = propName;
          break;
        }
      }
      if (propertyName) {
        if (propertyName.startsWith('__')) propertyName = propertyName.replace(/^__/, '');
        this._propertyName = propertyName;
      }
    }
    return this._propertyName ?? null;
  }

  constructor(host: ReactiveElement) {
    // NOTE: types will break when BuchungsheldLitElement is used here
    if (!(host instanceof BuchungsheldLitElement)) {
      throw new Error('BuchungsheldLitElement is required');
    }
    this.host = host;
    this.subject = new Subject<boolean>();
    this.host.addController(this);
  }

  hostConnected(): void {
    const sp = routingState.getValue().searchParams;

    // prevent users from going back to payment-flow
    if (sp.get('__completed') === 'true') {
      window.location.href = '/';
      return;
    }

    // handle return from stripe
    if (sp.get('step') === 'paid') {
      this.paymentLoading = true;
      void this._processStripePayment();
    }

    currentUser<User>()
      .isLoading.pipe(takeUntil(this.host.disconnected))
      .subscribe(userLoading => {
        this.userLoading = userLoading;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    currentUser<User>()
      .pipe(takeUntil(this.host.disconnected))
      .subscribe(user => {
        this.currentUser = user;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.step$.pipe(distinctUntilChanged(), takeUntil(this.host.disconnected)).subscribe(step => {
      this.step = step;
      this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
    });

    this.registrationData$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(registrationData => {
        this.registrationData = registrationData;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.institutionID$.pipe(distinctUntilChanged(), takeUntil(this.host.disconnected)).subscribe(institutionID => {
      if (!institutionID) return;
      this.institutionID = institutionID;
      this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
    });

    this.institution$.pipe(takeUntil(this.host.disconnected)).subscribe(institution => {
      if (!institution) return;
      this.institution = institution;
      this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      if (institution.maintenanceMode) {
        setSearchParam('step', 'maintainanceBlock');
        this.step = 'maintainanceBlock';
      }
    });

    this.participationIDs$.pipe(takeUntil(this.host.disconnected)).subscribe(participationIDs => {
      this.participationIDs = participationIDs;
      this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
    });

    this.stepInfo$.pipe(distinctUntilChanged(), takeUntil(this.host.disconnected)).subscribe(stepInfo => {
      this.stepInfo = stepInfo;
      this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
    });

    this.selectedProducts$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(selectedProducts => {
        this.selectedProducts = selectedProducts;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.selectedAppointments$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.selectedBlockingTimeslots$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(selectedBlockingTimeslots => {
        this.selectedBlockingTimeslots = selectedBlockingTimeslots;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.bookedAndBlockingTimeslots$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(bookedAndBlockingTimeslots => {
        this.bookedAndBlockingTimeslots = bookedAndBlockingTimeslots;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.allBlockingTimeslots$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(async allBlockingTimeslots => {
        this.allBlockingTimeslots = allBlockingTimeslots;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    this.activeParticipations$
      .pipe(distinctUntilChanged(), takeUntil(this.host.disconnected))
      .subscribe(activeParticipations => {
        this.activeParticipations = activeParticipations;
        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });

    const bundles$ = this.institution$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      switchMap(institution =>
        Bundle.subscribeAllBundlesByInstitution(
          0,
          99999,
          new BHFilter({ institutionID: institution._id }),
        )({ productIDs: 1 }),
      ),
    );

    combineLatest([this.registrationData$, bundles$])
      .pipe(takeUntil(this.host.disconnected))
      .subscribe(([productData, validBundles]) => {
        const productIDs = productData?.productData.map(data => data.productID) ?? [];
        const bundles = Bundle.getSelectedBundles(validBundles, productIDs as string[]);

        this.selectedBundles = bundles;
        this.bundleMappedByProductID = bundles.reduce<{ [k in string]: Bundle }>((idMappedProds, curBundle) => {
          const curBundleProdsMapped = curBundle.productIDs.map(curProdID => [curProdID, curBundle]);
          return { ...idMappedProds, ...Object.fromEntries(curBundleProdsMapped) };
        }, {});

        this._emitUpdate(); // ? Cannot detect changes in lifecycle methods. Do we need that?
      });
  }

  private _emitUpdate() {
    this.subject.next(true);
    this.host.requestUpdate();
  }

  get hasError() {
    return (
      !this.institutionID ||
      this.error ||
      this.currentUser?.suspentions?.map(suspension => suspension.institutionID).includes(this.institutionID) ||
      this.step === 'paid'
    );
  }

  private _parseDataQueryParamRegistrationData(dataQueryParam?: string) {
    try {
      if (!dataQueryParam) return null;
      const data = JSON.parse(dataQueryParam) as EntityData<RegistrationData>;
      return constructValue(data) as RegistrationData;
    } catch (e) {
      console.error('Es gab ein Problem beim parsen der QueryParams bei der Registrierung');
      console.error(e);
      this.error = new InvalidRegistrationDataError(
        'Es gab ein Problem beim parsen der QueryParams bei der Registrierung: ' + e?.message,
      );
    }
    return null;
  }

  get numSignups() {
    return this.activeParticipations.length + (this.registrationData?.productData.length ?? 0);
  }

  async _next() {
    this.host.scrollIntoView({ behavior: 'smooth' }); // important for embedds
    switch (this.step) {
      case 'workshop':
        const useLdapFlow = !this.stepInfo.userIsLoggedIn && this.stepInfo.requiresLDAP;
        if (useLdapFlow) setSearchParam('step', 'auth');
        else setSearchParam('step', 'personal');
        return;
      case 'auth':
        setSearchParam('step', 'personal');
        return;
      case 'personal': {
        if (!this.registrationData) throw new InvalidMissingRegistrationDataError();
        if (this.currentUser?.emailConfirmed) setSearchParam('step', 'terms');
        else setSearchParam('step', 'verifyEmail');
        return;
      }
      case 'verifyEmail': {
        setSearchParam('step', 'terms');
        return;
      }
      case 'terms': {
        const user = this.currentUser;
        if (!user) throw new Error('user is not defined');
        const billData = await getBillData(this.selectedProducts, this.selectedBundles ?? [], user._id);
        const paymentRequired = billData.totalPrice > 0;
        if (paymentRequired) setSearchParam('step', 'payment');
        else {
          // if no payment is required, skip payment screen and book directly
          await this.enrollIntoProduct();
          setSearchParam('step', 'booked');
        }
        return;
      }
      case 'payment':
        setSearchParam('step', 'booked');
        return;
      case 'booked':
        goTo('/profile');
        return;
    }

    console.error('step not handled:', this.step);
    throw new Error('unreachable');
  }

  async next() {
    this.nextStepProcessing = true;

    try {
      await this._next();
    } catch (e) {
      console.error('Es gab ein Problem beim Buchen der ausgewählten Veranstaltungen');
      console.error(e);
      this.error = e as Error;
      this.bookingProcessing = false;
    }

    this.nextStepProcessing = false;
  }

  async previous() {
    switch (this.step) {
      case 'workshop':
        goTo('/');
        return;
      case 'auth':
      case 'personal':
        setSearchParam('step', 'workshop');
        return;
      case 'verifyEmail':
        setSearchParam('step', 'personal');
        return;
      case 'terms':
        setSearchParam('step', 'personal');
        return;
      case 'payment':
        setSearchParam('step', 'terms');
        return;
      case 'booked':
        this.error = new InvalidGoBackAfterBookingError();
        return;
    }
    throw new Error('unreachable');
  }

  toggleSelectProdukt(
    products: NarrowedEntity<Product, { _id: 1 }> | NarrowedEntity<Product, { _id: 1 }>[],
    {
      timeslot,
      forceTrue,
      forceFalse,
    }: { timeslot?: NarrowedEntity<EinzelberatungSlot, { _id: 1 }>; forceTrue?: boolean; forceFalse?: boolean } = {},
  ) {
    const registrationData =
      this.registrationData ??
      new RegistrationData({
        institutionID: this.institutionID,
      });
    products = Array.isArray(products) ? products : [products];

    for (const product of products) {
      const index = registrationData.productData.findIndex(productData => productData.productID === product._id);
      const itemIsCurrentlySelected = index !== -1;
      if (itemIsCurrentlySelected && forceTrue) continue;
      if (!itemIsCurrentlySelected && forceFalse) continue;

      // remove selection
      if (index >= 0) {
        if (product instanceof Workshop) registrationData.productData.splice(index, 1);
        else {
          if (
            timeslot &&
            (registrationData.productData[index] as RegistrationEinzelberatungData).timeslotID !== timeslot._id
          )
            (registrationData.productData[index] as RegistrationEinzelberatungData).timeslotID = timeslot._id;
          else registrationData.productData.splice(index, 1);
        }

        // add selection
      } else if (
        this.institution.maximumParallelSignups &&
        this.numSignups >= this.institution.maximumParallelSignups
      ) {
        console.log('maximum parallel signups reached');
        void XSnackbar.show('Die maximale Anzahl an Parallelanmeldungen wurde erreicht');
      } else {
        if (product instanceof Workshop)
          registrationData.productData.push(new RegistrationWorkshopData({ productID: product._id }));
        else if (timeslot)
          registrationData.productData.push(
            new RegistrationEinzelberatungData({ productID: product._id, timeslotID: timeslot._id }),
          );
      }
    }

    // write to searchParams
    setSearchParam('data', JSON.stringify(registrationData.toJSON()));
  }

  async updateStammdaten(stammdaten: UserStammdaten) {
    if (!this.registrationData) throw new InvalidMissingRegistrationDataError();
    this.registrationData.userStammdaten = stammdaten;
    try {
      await this.registrationData.userStammdaten.save();
    } catch {
      await this.registrationData.userStammdaten.create();
    }
    setSearchParam('data', JSON.stringify(this.registrationData));
  }

  async updateUser(user: User) {
    if (!this.registrationData) return (this.error = new InvalidMissingRegistrationDataError());
    this.registrationData.user = user;
    await this.registrationData.user.save();
    setSearchParam('data', JSON.stringify(this.registrationData));
  }

  acceptTerms(accept: boolean) {
    if (!this.registrationData) return (this.error = new InvalidMissingRegistrationDataError());
    this.registrationData.acceptedTerms = accept;
    setSearchParam('data', JSON.stringify(this.registrationData));
  }

  private async enrollIntoProduct(paymentMethod?: string) {
    if (!this.registrationData) throw new InvalidMissingRegistrationDataError();

    const bookingInfo = await book(this.registrationData.productData, paymentMethod)(BookingResult.allFields);
    await invisiblyLoginUserWithToken(bookingInfo.loginToken);

    void XSnackbar.show('Erfolgreich gebucht!');
    setSearchParam('participationIDs', bookingInfo.participationIDs.join(','));
    if (bookingInfo.paymentIDs) setSearchParam('paymentIDs', bookingInfo.paymentIDs.join(','));

    return bookingInfo;
  }

  async pay(paymentMethod: string) {
    this.bookingProcessing = true;
    this._emitUpdate();

    const result = await this.enrollIntoProduct(paymentMethod);
    const paymentPlugin = getPluginForScope('payment', this.institution._id);

    if (!result.paymentIDs) throw new Error('paymentIDs are undefined');
    for (const paymentID of result.paymentIDs) await paymentPlugin.pay(paymentID, paymentMethod);

    this.bookingProcessing = false;
    this._emitUpdate();
    this.next();
  }

  private async _processStripePayment() {
    if (routingState.searchParams.getOnce('canceledPayment')) {
      this.error = new CancelledPaymentError();
      this.paymentLoading = false;
      return;
    }

    const paymentID = routingState.searchParams.getOnce('id');
    if (!paymentID)
      return (this.error = new InvalidRegistrationDataError(
        `Error processing stripe payment (no paymentID // ${window.location.search})`,
      ));
    const registrationData = await RegistrationData.getRegistrationDataFromPaymentID(paymentID)(
      RegistrationData.allFields,
    );

    setSearchParam('data', JSON.stringify(registrationData.toJSON()));
    setSearchParam('step', 'booked');
    this.paymentLoading = false;
  }

  // TODO: do not expose this method, use normal pay method a detect that there is payment that can be retried (also remove duplicated payment-link-creation-code)
  async _retryStripePayment() {
    const paymentID = routingState.searchParams.getOnce('id');
    if (!paymentID)
      return (this.error = new InvalidRegistrationDataError(
        'Error retrying stripe payment (no paymentID // ${window.location.search})',
      ));
    try {
      this.paymentLoading = true;
      await RegistrationData.retryPayment(paymentID);
    } catch (e) {
      this.paymentLoading = false;
      this.error = new InvalidRegistrationDataError(
        `Error retrying stripe payment (retryPayment Errored // ${window.location.search} // msg: ${e?.message})`,
      );
    }
  }

  showLoginScreen() {
    setSearchParam('step', 'auth');
    // const newSearchParams = new URLSearchParams(routingState.searchParams.getValue());
    // const searchParamsString = newSearchParams.toString();
    // setSearchParam('step', 'loginInNewTab');

    // window.open(`${baseConfig.get('ROOT_URL')}registration?${searchParamsString}`, '_blank');
  }

  goToPersonalData() {
    setSearchParam('step', 'personal');
  }

  resetProductSelection() {
    setSearchParam('step', 'workshop');
    if (this.registrationData) {
      this.registrationData.productData = [];
      setSearchParam('data', JSON.stringify(this.registrationData.toJSON()));
    }
  }
}

const invisiblyLoginUserWithToken = async (loginToken: string, showLoading?: boolean) => {
  const promise = User.loginWithToken(loginToken)()
    .then(async ([userID, userClass]) => {
      updateCurrentUser(undefined, false);
      // somehow currentUser call afterwards gets stuck without a small timeout, need to investigate later
      await firstValueFrom(timer(200));
      await _subscribeCurrentUser({ userID, userClass });
    })
    .then(() => firstValueFrom(currentUser().whenReady.pipe(filter(Boolean))));

  if (showLoading) return XDialog.waitFor(promise);
  return promise;
};

export class InvalidRegistrationDataError extends Error {}
export class InvalidStepError extends Error {}
export class InvalidMissingRegistrationDataError extends Error {}
export class InvalidGoBackAfterBookingError extends Error {}
export class CancelledPaymentError extends Error {}
